import React from 'react';
import 'twin.macro';
import {
  FicheDePrep,
  FDPPDFSettings,
  type ListsPrintView,
} from '@teetsh/app/src/types/sequences';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { SwitchToggle } from '@teetsh/app/src/components/Form/SwitchToggle';
import { SidebarWithSections } from '@teetsh/app/src/components/SubSidebar/SidebarWithSections';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ArrowDownCircleIcon,
  CreditCardIcon,
  ListBulletIcon,
  PrinterIcon,
  QueueListIcon,
  ServerStackIcon,
  SwatchIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { FontsCoupleSelector } from '@teetsh/app/src/components/Form/FontsCoupleSelector';
import { FontSizeSelector } from '@teetsh/app/src/components/FontSizeSelector';
import { MarginsFieldWrapper } from '@teetsh/app/src/components/Margins';
import { ColorSelector } from '@teetsh/app/src/components/Form/ColorSelector';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';
import ButtonGroup from '@teetsh/app/src/components/Form/ButtonGroup';
import { FieldsToDisplay } from './FieldsToDisplay';
import Select from 'components/Form/Select2';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import Cookies from 'js-cookie';
import { useSendToCMSFicheDePrep } from '@teetsh/app/src/queries/fdp/useSendToCMSFicheDePrep';

interface Props {
  onDownload: () => void;
  isDownloading: boolean;
  isSequence: boolean;
  ficheDePrep?: FicheDePrep;
  ficheDePrepId?: string;
  limitedBgSet: boolean;
}

export function Sidebar(props: Props) {
  const { isDownloading, isSequence, limitedBgSet, onDownload, ficheDePrepId } =
    props;
  const { t } = useTranslation(['common', 'sequence']);
  const { values } = useFormState<FDPPDFSettings>();
  const { sendToCMS, isLoading: isSendingToCMS } = useSendToCMSFicheDePrep();

  const queryParams = new URLSearchParams(location.search);
  const showSendToCms =
    queryParams.get('sharable') === 'true' || Cookies.get('teetshadmin');

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
        {showSendToCms && ficheDePrepId && (
          <Button
            fill
            large
            tw="mt-2"
            loading={isSendingToCMS}
            onClick={() => sendToCMS({ ficheDePrepId })}
            icon={ServerStackIcon}
          >
            {t('common:buttons.sendToCMS')}
          </Button>
        )}
      </div>
      <SidebarWithSections.Section
        icon={QueueListIcon}
        title={t('common:pdf.elementsAndDisposition')}
      >
        <div>
          {isSequence && (
            <>
              <Field
                name="withFichesDePrep"
                render={({ input }) => (
                  <SwitchToggle
                    label={t('sequence:pdf.withFichesDePrep')}
                    size="sm"
                    onChange={input.onChange}
                    checked={input.value}
                  />
                )}
              />
              <Field
                name="onePageFdp"
                render={({ input }) => (
                  <SwitchToggle
                    label={t('sequence:pdf.displayOnePagePerSeance')}
                    size="sm"
                    onChange={input.onChange}
                    checked={input.value}
                  />
                )}
              />
            </>
          )}

          {(!isSequence || values.withFichesDePrep) && (
            <>
              <Field
                name="breakInsideAvoidPhases"
                render={({ input }) => (
                  <SwitchToggle
                    label={t('sequence:pdf.breakInsideAvoid')}
                    size="sm"
                    onChange={input.onChange}
                    checked={input.value === '' ? true : input.value}
                  />
                )}
              />
              <Field
                name="numberThePhases"
                render={({ input }) => (
                  <SwitchToggle
                    label={t('sequence:pdf.numberThePhases')}
                    size="sm"
                    onChange={input.onChange}
                    checked={input.value === '' ? false : input.value}
                  />
                )}
              />
            </>
          )}
        </div>
        <FormGroup label={t('sequence:pdf.listsPrintView')}>
          <Field
            name="listsPrintView"
            render={({ input: { onChange, value } }) => (
              <Select<ListsPrintView>
                onChange={val => onChange(val)}
                value={value || 'inline'}
                options={[
                  {
                    value: 'inline',
                    label: (
                      <div tw="flex">
                        <span tw="flex-1">
                          {t('sequence:pdf.listsPrintView.inline')}
                        </span>
                        <span tw="text-muted">A, B, C</span>
                      </div>
                    ),
                  },
                  {
                    value: 'inline-list',
                    label: (
                      <div tw="flex">
                        <span tw="flex-1">
                          {t('sequence:pdf.listsPrintView.inlineList')}
                        </span>
                        <span tw="text-muted">A • B • C</span>
                      </div>
                    ),
                  },
                  {
                    value: 'list',
                    label: (
                      <div tw="flex">
                        <span tw="flex-1">
                          {t('sequence:pdf.listsPrintView.list')}
                        </span>
                        <span tw="text-muted">
                          <ListBulletIcon tw="inline-block w-4 h-4" />
                        </span>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          />
        </FormGroup>
        <FormGroup label={t('sequence:pdf.elementsToDisplay')}>
          <FieldsToDisplay />
        </FormGroup>
        {(!isSequence || values.withFichesDePrep) && (
          <FormGroup label={t('sequence:pdf.phasesPrintView')}>
            <Field
              name="phasesPrintView"
              render={({ input: { onChange, value } }) => (
                <div tw="flex flex-wrap gap-2">
                  <Button
                    onClick={() => onChange('cards')}
                    active={!value || value === 'cards'}
                    tw="grow"
                    icon={CreditCardIcon}
                  >
                    {t('sequence:pdf.phasesPrintView.cards')}
                  </Button>
                  <Button
                    active={value === 'table'}
                    onClick={() => onChange('table')}
                    tw="grow"
                    icon={TableCellsIcon}
                  >
                    {t('sequence:pdf.phasesPrintView.table')}
                  </Button>
                </div>
              )}
            />
          </FormGroup>
        )}
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="size"
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.mainTagColor')}>
          <Field
            name="mainTagColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                limitedBgSet={limitedBgSet}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.format')}>
          <Field
            name="landscape"
            render={({ input }) => (
              <ButtonGroup>
                <Button
                  active={!input.value}
                  onClick={() => input.onChange(false)}
                >
                  {t('common:pdf.portrait')}
                </Button>
                <Button
                  active={input.value}
                  onClick={() => input.onChange(true)}
                >
                  {t('common:pdf.landscape')}
                </Button>
              </ButtonGroup>
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          defaultValue={true}
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
